import { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import { forgotPasswordSchema } from "../../schema/auth";
import { Link, useNavigate } from "react-router-dom";
import { FaSignInAlt, FaUserPlus } from "react-icons/fa";
import axios from "axios";
import { toast } from "react-toastify";
import { PROXY } from "../../lib/utils";

const ForgotPassword = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    mode: "onChange",
    resolver: yupResolver(forgotPasswordSchema),
  });

  const onSubmit = async ({ email }) => {
    try {
      await axios.post(`${PROXY}/auth/forgot-password`, { email });
      toast.success("If the account exists, a password reset link has been sent.");
    } catch {
      toast.error("Unable to request a reset link. Please try again later.");
    }
  };

  return (
    <section className="flex items-center justify-center min-h-screen px-4">
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="relative flex flex-col text-gray-700 bg-white shadow-md w-96 rounded-xl bg-clip-border mx-4"
      >
        <div className="relative text-center grid mx-4 mb-4 -mt-6 overflow-hidden text-white shadow-lg h-28 place-items-center rounded-xl bg-gradient-to-tr from-indigo-900 to-indigo-800 bg-clip-border shadow-gray-900/20">
          <h3 className="block font-sans text-3xl antialiased font-semibold leading-snug tracking-normal text-white">
            {t("forgot_password")}
            <p className="text-sm text-center mt-2 tracking-wide">
              {t("app-name")}
            </p>
          </h3>
        </div>

        <div className="flex flex-col gap-4 p-6">
          <p className="text-sm text-gray-600 -mt-1 mb-1">
            {t("forgot_password_helper")}
          </p>

          <div
            dir={i18n.language === "en" ? "ltr" : "rtl"}
            className="relative h-11 w-full min-w-[200px] mb-3"
          >
            <input
              {...register("email")}
              className={`w-full h-full px-3 py-3 font-sans text-sm font-normal transition-all bg-transparent border rounded-md peer border-t-transparent text-blue-gray-700 outline outline-0 placeholder-shown:border placeholder-shown:border-blue-gray-200 placeholder-shown:border-t-blue-gray-200 focus:border-2 focus:border-t-transparent focus:outline-0 disabled:border-0 disabled:bg-blue-gray-50 ${
                errors.email ? "focus:border-red-500" : "focus:border-gray-900"
              }`}
              placeholder=""
            />
            {errors.email && (
              <p className="text-xs text-red-500 px-1 my-1">
                {errors.email.message}
              </p>
            )}
            <label
              type="email"
              className={`before:content[' '] after:content[' '] pointer-events-none absolute left-0 -top-1.5 flex h-full w-full select-none !overflow-visible truncate text-[11px] font-normal leading-tight text-gray-500 transition-all before:pointer-events-none before:mt-[6.5px] before:mr-1 before:box-border before:block before:h-1.5 before:w-2.5 before:rounded-tl-md before:border-t before:border-l before:border-transparent before:transition-all after:pointer-events-none after:mt-[6.5px] after:ml-1 after:box-border after:block after:h-1.5 after:w-2.5 after:flex-grow after:rounded-tr-md after:border-t after:border-r after:border-transparent after:transition-all peer-placeholder-shown:text-sm peer-placeholder-shown:leading-[4.1] peer-placeholder-shown:text-blue-gray-500 peer-placeholder-shown:before:border-transparent peer-placeholder-shown:after:border-transparent peer-focus:text-[11px] peer-focus:leading-tight peer-focus:text-gray-900 peer-focus:before:border-t-2 peer-focus:before:border-l-2 peer-focus:after:border-t-2 peer-focus:after:border-r-2 peer-focus:after:!border-gray-900 peer-disabled:text-transparent peer-disabled:before:border-transparent peer-disabled:after:border-transparent peer-disabled:peer-placeholder-shown:text-blue-gray-500 ${
                errors.email
                  ? "peer-focus:text-red-500 peer-focus:after:!border-red-500 before:border-blue-gray-200"
                  : ""
              }`}
            >
              {t("form.your-email")}
            </label>
          </div>
        </div>

        <div className="p-6 pt-0">
          <button
            disabled={isSubmitting}
            className="block w-full select-none rounded-lg bg-gradient-to-tr from-indigo-900 to-indigo-800 py-3 px-6 text-center align-middle font-sans text-xs font-bold uppercase text-white shadow-md shadow-indigo-900/10 transition-all hover:shadow-lg hover:shadow-indigo-900/20 active:opacity-[0.85] disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none"
            type="submit"
          >
            {isSubmitting ? t("form.sending") : t("send_reset_link")}
          </button>

          <div className="flex flex-col items-center gap-2 mt-6">
            <Link
              to="/login"
              className="inline-flex items-center gap-2 rounded-full px-4 py-2 bg-white border border-gray-200 text-sm font-medium text-gray-800 shadow-sm hover:shadow-md transition-shadow duration-150 hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-indigo-300"
            >
              <FaSignInAlt className="text-indigo-600" />
              <span>{t("sign_in")}</span>
            </Link>
            <Link
              to="/sign-up"
              className="inline-flex items-center gap-2 rounded-full px-4 py-2 bg-white border border-gray-200 text-sm font-medium text-gray-800 shadow-sm hover:shadow-md transition-shadow duration-150 hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-indigo-300"
            >
              <FaUserPlus className="text-indigo-600" />
              <span>{t("sign_up")}</span>
            </Link>
          </div>
        </div>
      </form>

      {isServiceModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 px-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <h4 className="text-xl font-semibold text-gray-900 mb-3">
              {t("forgot_password_service_notice_title")}
            </h4>
            <p className="text-sm text-gray-700 leading-6">
              {t("forgot_password_service_notice_message")}
            </p>

            <div className="mt-6 flex gap-3">
              <button
                type="button"
                className="flex-1 rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                onClick={() => setIsServiceModalOpen(false)}
              >
                {t("close")}
              </button>
              <button
                type="button"
                className="flex-1 rounded-lg bg-indigo-900 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-800"
                onClick={() => navigate("/contact")}
              >
                {t("go_to_contact_page")}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default ForgotPassword;
